/**
 * Watching a change of identity provider until it stops running.
 *
 * Declaring a choice does not return the result. It returns the state the
 * change **started** in, and the console re-reads the same block until it
 * settles: holding an HTTP request open across a restart of the tenancy's
 * control plane buys nothing, because a browser that gave up or a person who
 * closed the tab would leave the operation running with nobody watching.
 *
 * What it watches is `in_flight` and not the name of a state. The set of states
 * may grow; the question "is it still running" will not.
 *
 * A loop rather than an interval, and the waiting is injected: a test scripts
 * the answers and spends no real seconds, and the next read is never issued
 * before the previous one has come back.
 */

import type { RolloutBoard, TenancyIdentity } from './types';

export interface WatchOptions {
  /** Stops the watch. Nothing is reported after it fires. */
  signal: AbortSignal;
  /** How long between reads. */
  intervalMs?: number;
  /**
   * How many reads before giving up.
   *
   * A change holds the tenancy's lock for a bounded time and the server frees a
   * stale one by the clock, so a watch that never ends is watching something
   * that already ended. The screen still offers a re-read by hand.
   */
  maxReads?: number;
  /** Waits, or rejects when the watch is stopped. Injected for tests. */
  wait?: (ms: number, signal: AbortSignal) => Promise<void>;
}

/** How long between reads by default. */
export const WATCH_INTERVAL_MS = 2000;

/** Enough for ten minutes at the default interval — the server's own stale-lock window. */
export const WATCH_MAX_READS = 300;

/** `setTimeout`, aborted cleanly rather than left to fire into a dead component. */
export function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason as Error);
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(signal.reason as Error);
    }
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** How a watch ended. */
export type WatchOutcome =
  /** The change stopped running. The last block handed to `onStep` is the result. */
  | { done: 'settled' }
  /** The watch was stopped from outside. Nothing was reported after that. */
  | { done: 'stopped' }
  /** A read failed. The watch stops rather than hiding a broken connection. */
  | { done: 'failed'; error: unknown }
  /** It went on longer than a change can hold the tenancy's lock. */
  | { done: 'gave_up' };

/**
 * Re-reads until the change stops running, handing every answer to `onStep`.
 *
 * The first read happens after one interval: the caller already holds the block
 * that started the watch, so reading it again straight away would only ask the
 * server what it just said.
 */
export async function watchIdentity(
  read: (signal: AbortSignal) => Promise<TenancyIdentity>,
  onStep: (status: TenancyIdentity) => void,
  options: WatchOptions,
): Promise<WatchOutcome> {
  const { signal } = options;
  const interval = options.intervalMs ?? WATCH_INTERVAL_MS;
  const limit = options.maxReads ?? WATCH_MAX_READS;
  const pause = options.wait ?? sleep;

  for (let read_count = 0; read_count < limit; read_count += 1) {
    try {
      await pause(interval, signal);
    } catch {
      return { done: 'stopped' };
    }
    if (signal.aborted) return { done: 'stopped' };

    let status: TenancyIdentity;
    try {
      status = await read(signal);
    } catch (error: unknown) {
      if (signal.aborted) return { done: 'stopped' };
      return { done: 'failed', error };
    }
    if (signal.aborted) return { done: 'stopped' };

    onStep(status);
    if (!status.in_flight) return { done: 'settled' };
  }
  return { done: 'gave_up' };
}

/** How long between reads of the version board while a wave is moving. */
export const ROLLOUT_INTERVAL_MS = 5000;

/** A wave moves for minutes, not hours; this is an hour at the interval above. */
export const ROLLOUT_MAX_READS = 720;

/**
 * Re-reads the version board while a wave is moving.
 *
 * It lives here rather than in the screen because it is part of the contract
 * and not a habit of a page: starting a rollout and moving a wave's stragglers
 * both answer `202` with the board as it stood when the wave *began* moving, so
 * the answer to "what happened" only exists on a later read. Re-reading costs
 * nobody anything — the board is assembled from the control plane's own
 * storage, and no tenancy's cluster is asked anything by it.
 *
 * It stops when the board stops moving, and that judgement is the caller's:
 * which states count as moving belongs with the rest of the board's rules, not
 * in the transport.
 */
export async function watchRollout(
  read: (signal: AbortSignal) => Promise<RolloutBoard>,
  onBoard: (board: RolloutBoard) => void,
  options: WatchOptions & { stillMoving: (board: RolloutBoard) => boolean },
): Promise<WatchOutcome> {
  const { signal, stillMoving } = options;
  const interval = options.intervalMs ?? ROLLOUT_INTERVAL_MS;
  const limit = options.maxReads ?? ROLLOUT_MAX_READS;
  const pause = options.wait ?? sleep;

  for (let reads = 0; reads < limit; reads += 1) {
    try {
      await pause(interval, signal);
    } catch {
      return { done: 'stopped' };
    }
    if (signal.aborted) return { done: 'stopped' };

    let board: RolloutBoard;
    try {
      board = await read(signal);
    } catch {
      if (signal.aborted) return { done: 'stopped' };
      // A refresh that did not arrive changes nothing on screen: the board
      // stays as it was and the next read tries again. A wave being moved is
      // exactly when the control plane is busiest, and giving up on the first
      // refused read would leave the screen frozen at the least useful moment.
      continue;
    }
    if (signal.aborted) return { done: 'stopped' };

    onBoard(board);
    if (!stillMoving(board)) return { done: 'settled' };
  }
  return { done: 'gave_up' };
}
